import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ScanBarcode, Printer, Ban, Trash2, Mail, Lock } from "lucide-react";
import { api, errMsg, eur, today, download } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Display, Keypad, QuickKeys, REPARTI } from "@/components/CassaKeys";

const lordo = (p) => +(Number(p.prezzo || 0) * (1 + Number(p.aliquota || 0) / 100)).toFixed(2);
const importo = (r) => Math.max(0, Number(r.quantita || 0) * Number(r.prezzo || 0) - Number(r.sconto_importo || 0));
const cents = (n) => Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const totali = (rows) => {
  let imp = 0; let iva = 0;
  rows.forEach((r) => { const l = importo(r); const a = Number(r.aliquota) || 0; const i = l / (1 + a / 100); imp += i; iva += l - i; });
  return { imp, iva, tot: imp + iva };
};

const Search = ({ onPick }) => {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  const ref = useRef(null);
  const last = useRef("");
  useEffect(() => { ref.current?.focus(); }, []);
  const find = async (text, exactOnly) => {
    last.current = text;
    if (!text.trim()) return setRes([]);
    try {
      const { data } = await api.get("/corrispettivi/cerca", { params: { q: text } });
      if (last.current !== text) return;
      if (exactOnly && data.length === 1 && (data[0].barcode === text.trim() || (data[0].codice || "").toLowerCase() === text.trim().toLowerCase())) {
        onPick(data[0]); last.current = ""; setQ(""); setRes([]); return;
      }
      setRes(data);
      if (exactOnly && !data.length) toast.error(`Nessun articolo con codice ${text}`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="relative">
      <ScanBarcode className="absolute left-3 top-2.5 h-5 w-5 text-slate-500" />
      <Input ref={ref} data-testid="pos-scan-input" className="h-10 bg-white pl-10 font-mono" placeholder="Spara il codice a barre o cerca un articolo memorizzato" value={q}
        onChange={(e) => { setQ(e.target.value); if (e.target.value.length >= 2) find(e.target.value, false); else { last.current = ""; setRes([]); } }}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); find(q, true); } }} />
      {res.length > 0 && (
        <div className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg" data-testid="pos-search-results">
          {res.map((p) => (
            <button key={p.id} type="button" data-testid={`pos-result-${p.id}`} className="flex w-full justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-sky-50"
              onClick={() => { last.current = ""; onPick(p); setQ(""); setRes([]); ref.current?.focus(); }}>
              <span><span className="font-mono text-xs text-slate-600">{p.barcode || p.codice}</span> {p.descrizione}</span><span className="font-mono">{eur(lordo(p))}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Scontrino = ({ rows, remove }) => (
  <div className="min-h-[220px] rounded-lg border border-sky-200 bg-white p-3 font-mono text-sm" data-testid="pos-receipt">
    {rows.length === 0 ? <p className="py-8 text-center font-sans text-slate-500">Scontrino vuoto: digita un importo e premi un reparto, usa un tasto rapido o spara un codice</p> : rows.map((r, i) => (
      <div key={i} className="flex items-center gap-2 border-b border-dashed border-slate-200 py-1.5" data-testid={`pos-cart-row-${i}`}>
        <div className="min-w-0 flex-1"><div className="truncate font-sans font-medium">{r.descrizione}</div>
          <div className="text-xs text-slate-500">{r.quantita} x {cents(r.prezzo)} · {Number(r.aliquota) ? `${r.aliquota}%` : r.natura}</div></div>
        <span className="font-semibold" data-testid={`pos-cart-total-${i}`}>{cents(importo(r))}</span>
        <button type="button" className="text-slate-400 hover:text-rose-600" data-testid={`pos-cart-remove-${i}`} onClick={() => remove(i)}><Trash2 className="h-4 w-4" /></button>
      </div>
    ))}
  </div>
);

const Pagamento = ({ id, label, on, setOn, val, setVal }) => (
  <div className="flex items-center gap-2">
    <Checkbox id={id} checked={on} onCheckedChange={(v) => setOn(!!v)} data-testid={`${id}-check`} className="h-5 w-5 border-slate-500 bg-white data-[state=checked]:bg-blue-700" />
    <label htmlFor={id} className="min-w-0 flex-1 text-xs font-bold uppercase leading-tight text-slate-800">{label}</label>
    <Input className="h-9 w-24 shrink-0 bg-white text-right font-mono" type="number" step="0.01" disabled={!on} data-testid={`${id}-input`} placeholder="€" value={on ? val : ""} onChange={(e) => setVal(e.target.value)} />
  </div>
);

const inviaEmail = async (id, email) => {
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim())) { toast.error("Email del cliente non valida"); return false; }
  try { await api.post(`/corrispettivi/vendite/${id}/email`, { email: email.trim() }); toast.success(`Scontrino inviato a ${email.trim()}`); return true; } catch (e) { toast.error(errMsg(e)); return false; }
};

const chiusura = async (onDone) => {
  const d = today();
  if (!window.confirm("Chiudere la giornata di oggi? Dopo la chiusura non si potranno registrare altre vendite con questa data.")) return;
  try { await api.post(`/corrispettivi/chiusura/${d}`); toast.success("Giornata chiusa"); } catch (e) {
    if (!/già chiusa/i.test(errMsg(e))) return toast.error(errMsg(e));
    toast.info("La giornata era già chiusa: stampo il riepilogo");
  }
  download(`/corrispettivi/chiusura/${d}/pdf`).catch((e) => toast.error(e.message));
  onDone();
};

const VenditaRow = ({ v, onChange }) => {
  const [rt, setRt] = useState(v.rt_numero || "");
  const saveRt = async () => { try { await api.patch(`/corrispettivi/vendite/${v.id}`, { rt_numero: rt }); toast.success("Numero documento commerciale salvato"); onChange(); } catch (e) { toast.error(errMsg(e)); } };
  const annulla = async () => {
    if (!window.confirm(`Annullare la vendita ${v.numero}?`)) return;
    try { await api.post(`/corrispettivi/vendite/${v.id}/annulla`); toast.success("Vendita annullata"); onChange(); } catch (e) { toast.error(errMsg(e)); }
  };
  const off = v.stato === "annullata";
  return (
    <div className={`flex flex-wrap items-center gap-3 border-b border-slate-100 py-2 text-sm ${off ? "opacity-50" : ""}`} data-testid={`sale-row-${v.id}`}>
      <span className="w-20 font-mono font-semibold">{v.numero}</span>
      <span className="w-24 text-right font-mono">{eur(v.totale)}</span>
      <span className="text-xs text-slate-600">{v.contanti ? `Contanti ${eur(v.contanti)} ` : ""}{v.elettronico ? `Elettronico ${eur(v.elettronico)} ` : ""}{v.bonus ? `Bonus ${eur(v.bonus)}` : ""}</span>
      {off ? <span className="text-xs font-semibold text-rose-600">Annullata</span> : (
        <div className="ml-auto flex items-center gap-2">
          <Input className="h-8 w-44 font-mono text-xs" placeholder="N. documento RT" data-testid={`sale-rt-input-${v.id}`} value={rt} onChange={(e) => setRt(e.target.value)} onBlur={() => rt !== (v.rt_numero || "") && saveRt()} />
          <Button variant="ghost" size="icon" title={v.email_inviata ? `Inviato a ${v.email_inviata}: invia di nuovo` : "Invia scontrino via email"} data-testid={`sale-email-${v.id}`}
            className={v.email_inviata ? "text-blue-700" : ""} onClick={() => { const m = window.prompt("Email del cliente", v.email_inviata || ""); if (m) inviaEmail(v.id, m).then((ok) => ok && onChange()); }}><Mail className="h-4 w-4" /></Button>
          <Button variant="ghost" size="icon" title="Promemoria PDF" data-testid={`sale-pdf-${v.id}`} onClick={() => download(`/corrispettivi/vendite/${v.id}/pdf`).catch((e) => toast.error(e.message))}><Printer className="h-4 w-4" /></Button>
          <Button variant="ghost" size="icon" title="Annulla vendita" className="text-slate-400 hover:text-rose-600" data-testid={`sale-cancel-${v.id}`} onClick={annulla}><Ban className="h-4 w-4" /></Button>
        </div>
      )}
    </div>
  );
};

const usePagamenti = () => {
  const init = { contanti: [true, ""], elettronico: [false, ""], bonus: [false, ""] };
  const [p, setP] = useState(init);
  const [last, setLast] = useState("contanti");
  const on = (k, v) => { setP({ ...p, [k]: [v, v ? "" : p[k][1]] }); if (v) setLast(k); };
  return { p, last, on, val: (k, v) => setP({ ...p, [k]: [p[k][0], v] }), reset: () => { setP(init); setLast("contanti"); } };
};

const KEYS = ["contanti", "elettronico", "bonus"];
const auto = (pg, tot) => {
  const vuoti = KEYS.filter((k) => pg.p[k][0] && pg.p[k][1] === "");
  const scritti = KEYS.reduce((s, k) => s + (pg.p[k][0] && pg.p[k][1] !== "" ? Number(pg.p[k][1]) || 0 : 0), 0);
  const chi = vuoti.includes(pg.last) ? pg.last : vuoti[0];
  return Object.fromEntries(KEYS.map((k) => [k, !pg.p[k][0] ? 0 : pg.p[k][1] !== "" ? Number(pg.p[k][1]) || 0 : k === chi ? Math.max(0, +(tot - scritti).toFixed(2)) : 0]));
};

const useTastiera = (onReparto) => {
  const [buffer, setBuffer] = useState("");
  const [qty, setQty] = useState(1);
  const key = (k) => {
    if (k === "C") { if (buffer) setBuffer(""); else setQty(1); return; }
    if ((buffer + k).length <= 8) setBuffer((buffer + k).replace(/^0+(?=\d)/, ""));
  };
  const times = () => { setQty(Math.max(1, parseInt(buffer || "1", 10))); setBuffer(""); };
  const reset = () => { setBuffer(""); setQty(1); };
  const reparto = (k) => {
    const amount = Number(buffer || 0) / 100;
    if (!amount) return toast.error("Digita prima l'importo, poi premi il reparto");
    onReparto(k, amount, qty); reset();
  };
  return { buffer, qty, key, times, reparto, reset };
};

export const Cassa = ({ write }) => {
  const [rows, setRows] = useState([]);
  const [sales, setSales] = useState([]);
  const [tasti, setTasti] = useState(Array(10).fill(null));
  const [products, setProducts] = useState([]);
  const [ultima, setUltima] = useState("");
  const [busy, setBusy] = useState(false);
  const [mail, setMail] = useState("");
  const pg = usePagamenti();
  const add = (r) => { setRows((x) => [...x, r]); setUltima(`${r.descrizione.slice(0, 18)} ${cents(r.prezzo * r.quantita)}`); };
  const kb = useTastiera((k, amount, qty) => {
    const nat = k.startsWith("N");
    add({ product_id: "", codice: "", descrizione: `Reparto ${REPARTI.find((x) => x[0] === k)[1]}`, quantita: qty, prezzo: amount, aliquota: nat ? 0 : Number(k), natura: nat ? k : "", sconto_importo: 0 });
  });
  const load = () => api.get("/corrispettivi/vendite", { params: { data: today() } }).then((r) => setSales(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/corrispettivi/tasti").then((r) => setTasti(r.data)).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);
  const pick = (p) => {
    const prezzo = kb.buffer ? Number(kb.buffer) / 100 : lordo(p);
    add({ product_id: p.id, codice: p.codice || "", descrizione: p.descrizione, quantita: kb.qty, prezzo, aliquota: Number(p.aliquota || 0), natura: p.natura || "", sconto_importo: 0 });
    kb.reset();
  };
  const saveTasti = (ids) => api.put("/corrispettivi/tasti", { product_ids: ids }).then((r) => { setTasti(r.data); toast.success("Tasti rapidi salvati"); }).catch((e) => toast.error(errMsg(e)));
  const t = totali(rows);
  const pay = auto(pg, t.tot);
  const emetti = async () => {
    if (mail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(mail.trim())) return toast.error("Email del cliente non valida: correggila o lasciala vuota");
    setBusy(true);
    try {
      const { data } = await api.post("/corrispettivi/vendite", { righe: rows, ...pay });
      toast.success(`Vendita ${data.numero} registrata${data.resto > 0 ? ` · resto ${eur(data.resto)}` : ""}. Ora batti lo scontrino sul registratore telematico.`);
      setRows([]); pg.reset(); kb.reset(); setUltima(data.resto > 0 ? `Resto ${cents(data.resto)}` : `Vendita ${data.numero}`);
      if (mail.trim() && await inviaEmail(data.id, mail)) setMail("");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4">
      <div className="grid gap-4 rounded-2xl border border-sky-300 bg-sky-100 p-4 shadow-xl lg:grid-cols-12" data-testid="pos-register">
        <div className="space-y-3 lg:col-span-5">
          <Display buffer={kb.buffer} qty={kb.qty} totale={t.tot} ultima={ultima} />
          {write && <Keypad onKey={kb.key} onTimes={kb.times} onReparto={kb.reparto} />}
          {write && <p className="text-xs text-slate-600">Digita l'importo e premi il reparto. Per la quantità digita il numero e premi X; un importo digitato prima di un tasto rapido diventa il suo prezzo.</p>}
        </div>
        <div className="space-y-3 lg:col-span-4">
          {write && <Search onPick={pick} />}
          <Scontrino rows={rows} remove={(i) => setRows(rows.filter((_, k) => k !== i))} />
          <div className="flex justify-between rounded-lg bg-[#020617] px-4 py-2 text-sm font-semibold text-white">
            <span>Imponibile <span data-testid="pos-taxable" className="font-mono">{cents(t.imp)}</span></span><span>IVA <span data-testid="pos-vat" className="font-mono">{cents(t.iva)}</span></span>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1 bg-white" data-testid="pos-remove-last-button" onClick={() => setRows(rows.slice(0, -1))}>Storno ultima riga</Button>
            <Button variant="outline" className="flex-1 bg-white text-rose-700" data-testid="pos-reset-button" onClick={() => { setRows([]); pg.reset(); kb.reset(); }}>Annulla scontrino</Button>
          </div>
        </div>
        <div className="space-y-3 lg:col-span-3">
          <QuickKeys tasti={tasti} products={products} write={write} onPick={pick} onSave={saveTasti} />
          <div className="space-y-2 rounded-lg border border-sky-200 bg-white p-3">
            {[["contanti", "pos-cash", "Contanti"], ["elettronico", "pos-card", "Elettronico"], ["bonus", "pos-bonus", "Bonus"]].map(([k, id, l]) => (
              <Pagamento key={k} id={id} label={l} on={pg.p[k][0]} setOn={(v) => pg.on(k, v)} val={pg.p[k][0] && pg.p[k][1] === "" ? pay[k].toFixed(2) : pg.p[k][1]} setVal={(v) => pg.val(k, v)} />
            ))}
            <div className="relative pt-1">
              <Mail className="absolute left-2.5 top-3.5 h-4 w-4 text-slate-500" />
              <Input type="email" className="h-9 bg-white pl-8 text-sm" placeholder="Email cliente (facoltativa)" data-testid="pos-email-input" value={mail} onChange={(e) => setMail(e.target.value)} />
            </div>
          </div>
          <Button className="h-14 w-full rounded-xl bg-blue-700 text-lg font-bold text-white shadow-md hover:bg-blue-800" data-testid="pos-pay-button" disabled={!write || busy || rows.length === 0} onClick={emetti}>Emetti scontrino</Button>
          {write && <Button variant="outline" className="h-11 w-full rounded-xl border-slate-400 bg-white font-bold uppercase tracking-wide text-slate-800" data-testid="pos-daily-close-button" disabled={busy || rows.length > 0} onClick={() => chiusura(load)}><Lock className="mr-2 h-4 w-4" />Chiusura giornaliera</Button>}
        </div>
      </div>
      <p className="text-xs text-slate-600">Il documento commerciale va emesso sul tuo registratore telematico: poi riporta qui sotto il suo numero.</p>
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <h3 className="mb-2 text-sm font-semibold">Vendite di oggi</h3>
        {sales.length === 0 ? <p className="text-sm text-slate-600">Nessuna vendita oggi.</p> : sales.map((v) => <VenditaRow key={v.id} v={v} onChange={load} />)}
      </div>
    </div>
  );
};
